import Client, { Network } from "@helium/http";
import { XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import { useHotspots } from "../../../../context/hotspots";
import HotspotActivityTab from "../HotspotActivityTab";
import RealTimeHotspotDiagnostics from "./RealTimeHotspotDiagnostics";

const HotspotStatusTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { currentHotspot } = useHotspots();
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState<
    number | undefined
  >();
  const [lastHotspotActivity, setLastHotspotActivity] =
    useState<LastHotspotActivity>();

  const fetchLastHotspotActivity = useCallback(async (hotspotId: string) => {
    setLastHotspotActivity(undefined);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_ETL_API}/hotspot/last-activity/${hotspotId}`
      );

      const response: LastHotspotActivityResponse = await res.json();

      setLastHotspotActivity(response.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchLastActivityTimestamp = useCallback(async (hotspotId: string) => {
    setLastActivityTimestamp(undefined);

    const network = new Network({
      baseURL: process.env.REACT_APP_HELIUM_API_BASE_URL as string,
      version: 1
    });
    const client = new Client(network);
    const transactionList = await (
      await client.hotspot(hotspotId).roles.list()
    ).take(1);

    if (
      transactionList &&
      transactionList.length &&
      transactionList[0] &&
      transactionList[0].time
    ) {
      setLastActivityTimestamp(transactionList[0].time);
    }
  }, []);

  useEffect(() => {
    fetchLastHotspotActivity(props.match.params.id);
    fetchLastActivityTimestamp(props.match.params.id);
  }, [
    fetchLastHotspotActivity,
    fetchLastActivityTimestamp,
    props.match.params.id
  ]);

  const activityStatusDescription = useMemo(() => {
    if (currentHotspot && (lastActivityTimestamp || lastHotspotActivity)) {
      const mostRecentActivityTimestamp = Math.max(
        lastActivityTimestamp || 0,
        lastHotspotActivity?.timestamp || 0
      );

      return `Last active ${moment
        .unix(mostRecentActivityTimestamp)
        .utc()
        .fromNow()}`;
    }

    return null;
  }, [currentHotspot, lastActivityTimestamp, lastHotspotActivity]);

  const renderActivityStatusIcon = useMemo(() => {
    if (!currentHotspot || !(lastActivityTimestamp || lastHotspotActivity)) {
      return (
        <CenteredLoading className="w-4 h-4 text-gray-900 dark:text-white" />
      );
    }

    return currentHotspot.online ? (
      <CheckIcon className="h-5 text-green-500" />
    ) : (
      <XIcon className="h-5 text-red-500" />
    );
  }, [currentHotspot, lastActivityTimestamp, lastHotspotActivity]);

  if (!currentHotspot) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <RealTimeHotspotDiagnostics hotspot={currentHotspot} />

      <div className="mt-10 pb-5 border-b border-gray-200 dark:border-gray-500">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          Blockchain data
        </h3>
      </div>

      <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
        This section shows the hotspot information that is currently available
        in the Helium blockchain. Note that this might differ from the actual
        status of your hotspot as it takes time for information to propagate
        from your hotspot to the blockchain.
      </p>

      <div className="flex flex-col mt-6 items-start">
        <span className="text-lg font-medium mb-2">Hotspot status</span>

        <div className="flex items-center border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-1.5">
          <div className="flex-shrink-0">{renderActivityStatusIcon}</div>

          <span className="font-bold text-gray-900 dark:text-white ml-1.5">
            {currentHotspot.online ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="mt-6 text-lg font-medium mb-2">Hotspot activity</div>
      {activityStatusDescription && (
        <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          {activityStatusDescription}
        </p>
      )}
      <HotspotActivityTab hotspotId={currentHotspot.id} />
    </div>
  );
};

export default HotspotStatusTab;

//
// Utils
//

interface WitnessData {
  hash: string;
  distance: number;
  datarate: string;
  rssi: number;
  snr: number;
  frequency: number;
  valid: boolean;
  timestamp: number;
  channel: number;
  location: string;
  invalid_reason?: string;
}

interface Witness {
  hash: string;
  time: number;
  block: number;
  distance: number;
  challenger: string;
  challenger_location: string;
  beaconer: string;
  beaconer_location: string;
  valid: boolean;
  valid_count: number;
  invalid_count: number;
  witnesses: WitnessData[];
}

interface Challenger {
  hash: string;
  time: number;
  block: number;
  challenger: string;
  challenger_location: string;
  beaconer: string;
  beaconer_location: string;
  valid_count: number;
  invalid_count: number;
}

interface Challengee {
  hash: string;
  time: number;
  block: number;
  challenger: string;
  challenger_location: string;
  beaconer: string;
  beaconer_location: string;
  valid_count: number;
  invalid_count: number;
  witnesses: WitnessData[];
}

interface DataPacket {
  hash: string;
  times: number;
  block: number;
  num_dcs: number;
  location: string;
  num_packets: number;
}

interface Reward {
  hash: string;
  time: number;
  block: number;
  amount: number;
}

interface LastHotspotActivity {
  witnesses: Witness[];
  challengers: Challenger[];
  challengees: Challengee[];
  rewards: Reward[];
  data_packets: DataPacket[];
  timestamp: number;
}

interface LastHotspotActivityResponse {
  object: "list";
  url: "/hotspot/last-activity/112mnaE16ZkW7ZdG8yt3PXUqnNXZdTaisY5xRe7GZzNkZ7VPwNq3/";
  data: LastHotspotActivity;
}
